"use client";

import { ForgotPasswordIdentifierSchema, ForgotPasswordIdentifierType } from "@/lib/schema";
import { zodResolver } from "@hookform/resolvers/zod";
import React, { startTransition, useEffect, useState } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import Image from "next/image";
import { cn, getIdentifierType } from "@/lib/utils";
import { useTheme } from "next-themes";
import { ChevronLeftIcon } from "@heroicons/react/24/outline";
import { useRouter } from "next/navigation";
import { forgotPassword } from "@/services/auth";

export default function WorkerForgotPassword() {
  const [Loading, setLoading] = useState(false);
  const router = useRouter();
  const methods = useForm<ForgotPasswordIdentifierType>({ resolver: zodResolver(ForgotPasswordIdentifierSchema) });
  const {
    handleSubmit,
    formState: { errors },
    register,
    control,
  } = methods;

  const identifier = useWatch({ control, name: "identifier" });

  const isFormFilled = Boolean(identifier);
  const onSubmit = async (data: ForgotPasswordIdentifierType) => {
    setLoading(true);
    try {
      const identifierType = getIdentifierType(data.identifier);

      const { error } = await forgotPassword({ identifier: data.identifier });
      if (error) {
        setLoading(false);
        toast.error("An error occured");
        return;
      }
      toast.success("OTP has been sent to the registered contact.");

      router.push(
        `/forgot-password/otp${identifierType === "email" ? `?email=${encodeURIComponent(data.identifier)}` : identifierType === "phone" ? `?phone=${encodeURIComponent(data.identifier)}` : `?ippis=${encodeURIComponent(data.identifier)}`}`
      );
    } catch (error) {
      setLoading(false);
      // todo: placeholder error message, replace with actual error message
      toast.error(error instanceof Error ? error.message : "An error occured");
      return;
    }
  };
  const { theme } = useTheme();
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    startTransition(() => {
      setMounted(true);
    });
  }, []);

  return (
    <div className=''>
      <div
        className={cn(
          "text-link-primary mx-auto mb-8 flex w-fit items-center gap-1 text-4xl font-medium md:text-5xl lg:text-6xl 2xl:mt-[5%] 2xl:mb-12"
        )}
      >
        <Image
          src={mounted && theme === "dark" ? "/assets/icons/logo-dark.svg" : "/assets/icons/logo-light.svg"}
          alt='Onyx Logo'
          width={82}
          height={82}
          className='mr-2 inline-flex size-12 md:size-14 lg:size-16'
        />
        Onyx <span className='text-link-primary'>.</span>
      </div>
      <Button
        variant={"outline"}
        className='border-border-title/15 mb-8 h-8 cursor-pointer rounded border-[0.5px] px-3.5 md:h-8'
        onClick={() => router.back()}
      >
        <ChevronLeftIcon className='text-text-title/70 size-3 stroke-2' />
      </Button>
      <h1 className='text-link-primary text-2xl leading-[100%] font-semibold md:text-3xl'>Forget Password</h1>
      <p className='text-text-title mt-3 leading-[110%]'>
        Enter your email or IPPIS number and we&apos;ll send you a 6-digit password reset code.
      </p>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)} className='mx-auto mt-5 flex w-full flex-col gap-4.5'>
          <div className='flex flex-col gap-2.5'>
            <Label className='px-2.5 md:font-normal lg:font-normal'>Identifier</Label>
            <div className='relative w-full'>
              <Input
                aria-invalid={errors.identifier ? "true" : "false"}
                // type='number'
                className='px-6'
                placeholder='Input your Email or IPPIS number'
                {...register("identifier")}
              />
            </div>
            {errors.identifier && (
              <p aria-invalid='true' className='text-text-destructive text-xs'>
                {errors.identifier.message}
              </p>
            )}
          </div>

          <Button
            type='submit'
            loading={Loading}
            disabled={!isFormFilled || Loading}
            className='mx-auto mt-5 w-full py-6 text-xs lg:mt-0'
          >
            Continue
          </Button>
        </form>
      </FormProvider>
    </div>
  );
}
